/**
 * Teach it back (#5270): after a Super Section week, a learner teaches that
 * week's content to simulated students, and a judge checks the learner's
 * own lines for evidence of each outcome.
 *
 * - A week's spec ({@link TeachBackSpecSchema}) is readable by learners; its
 *   judge spec ({@link TeachBackJudgeSpecSchema}) is admin and server only.
 * - A learner's summary ({@link TeachBackSummarySchema}) and attempts
 *   ({@link TeachBackAttemptSchema}) are written only by the server. An
 *   attempt is a live session (recorded and judged) or an uploaded video
 *   (never judged).
 *
 * simteach mirrors the spec and judge spec in
 * `app/section_teaching/teach_back_spec.py`; both sides parse
 * `teachBackSeeds.json`.
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../firebase/types';
import {StudentSeedsSchema} from './studentSeed';

/** Subcollection of `course/{courseId}` holding one spec per teach-back. */
export const TEACH_BACKS_COLLECTION = 'teachBacks';

/** Subcollection of a spec holding its admin-only documents. */
export const TEACH_BACK_PRIVATE_COLLECTION = 'private';

/** The judge spec's id in {@link TEACH_BACK_PRIVATE_COLLECTION}. */
export const TEACH_BACK_JUDGE_DOC_ID = 'judge';

/** The learner's summary doc id under `users/{uid}/{courseId}`. */
export const TEACH_BACK_SUMMARY_DOC_ID = 'teachBack';

/** Subcollection of the summary doc holding one doc per attempt. */
export const TEACH_BACK_ATTEMPTS_COLLECTION = 'teachBackAttempts';

/** Storage folder holding attempt videos: session recordings and uploads. */
export const TEACH_BACK_RECORDINGS_FOLDER = 'teachBackRecordings';

/** The seat in focus when a spec names none. */
export const DEFAULT_TEACH_BACK_FOCUS_SEAT = 'walter';

/** Charged attempts allowed per week when a spec sets no cap. */
export const DEFAULT_TEACH_BACK_MAX_ATTEMPTS = 5;

/** Session length cap when a spec sets none. */
export const DEFAULT_TEACH_BACK_MAX_SESSION_SECONDS = 1200;

/** Most quotes kept as one outcome's evidence. */
export const TEACH_BACK_MAX_EVIDENCE_QUOTES = 3;

/** Most resolved misstatements kept for one outcome; open ones are all kept. */
export const TEACH_BACK_MAX_RESOLVED_MISSTATEMENTS = 5;

/** Longest quote the judge keeps, in characters. */
export const TEACH_BACK_MAX_QUOTE_CHARS = 400;

const KEBAB_CASE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** A teach-back spec's document id, e.g. `pai1-week1-two-outputs`. */
export const TeachBackIdSchema = z
  .string()
  .max(100)
  .regex(KEBAB_CASE, 'Use kebab-case');

/** A teach-back spec's document id. */
export type TeachBackId = z.infer<typeof TeachBackIdSchema>;

/** An outcome's id, unique within its spec, e.g. `independence`. */
export const TeachBackOutcomeIdSchema = z
  .string()
  .max(60)
  .regex(KEBAB_CASE, 'Use kebab-case');

/**
 * A section week, 1-based: the course's first section is week 1, as in the
 * `:sectionWeek` route parameter.
 */
export const TeachBackSectionWeekSchema = z.number().int().min(1);

/** A handbook chapter: `teacherhandbook/week{N}`. */
export const TeachBackSourceSchema = z
  .string()
  .regex(/^teacherhandbook\/week[1-9]\d*$/, 'Use teacherhandbook/week{N}');

/** One outcome as learners see it. */
export const TeachBackOutcomeSchema = z.strictObject({
  /** Unique within the spec; the judge spec uses the same ids. */
  id: TeachBackOutcomeIdSchema,
  /** What the learner should be able to explain, in learner-facing words. */
  learnerFacing: z.string().trim().min(1),
  /**
   * The id of the deck slide this outcome is taught on; a gap is worth asking
   * about once the session is past it. Omitted: judged by time instead.
   */
  slide: z.string().trim().min(1).optional(),
});

/** One outcome as learners see it. */
export type TeachBackOutcome = z.infer<typeof TeachBackOutcomeSchema>;

/** The slides the session shows. */
export const TeachBackDeckSchema = z.strictObject({
  /**
   * A deck under the course's own slide decks, or under the course it borrows
   * slides from.
   */
  deckId: z.string().trim().min(1),
  /** The slide the session opens on. Omitted: the first visible slide. */
  openAt: z.string().trim().min(1).optional(),
  /**
   * Opens that slide at its last build step, a scripted demo already played
   * through. Omitted or false: at its first step.
   */
  openAtEnd: z.boolean().optional(),
});

/** The slides a teach-back session shows. */
export type TeachBackDeck = z.infer<typeof TeachBackDeckSchema>;

/** One block of the focus seat's notebook: a line of text, or a table. */
export const TeachBackNotebookBlockSchema = z.union([
  z.strictObject({
    /** One line or short paragraph. */
    text: z.string().trim().min(1),
  }),
  z.strictObject({
    table: z.strictObject({
      /** Column headings. */
      header: z.array(z.string().trim().min(1)).min(1),
      /**
       * Rows, each with as many cells as `header`; an empty cell is "". Each
       * row is an object because Firestore stores no array inside an array.
       */
      rows: z.array(z.strictObject({cells: z.array(z.string())})).min(1),
    }),
  }),
]);

/** One block of the focus seat's notebook. */
export type TeachBackNotebookBlock = z.infer<
  typeof TeachBackNotebookBlockSchema
>;

/**
 * Adds an issue for every repeated `id` in `items`.
 *
 * @param items - Objects with an `id`.
 * @param ctx - The refinement context to report to.
 * @param path - Where `items` sits in the parsed value.
 * @return Nothing; issues go to `ctx`.
 * @throws Never.
 */
function addDuplicateIdIssues(
  items: readonly {id: string}[],
  ctx: z.RefinementCtx,
  path: (string | number)[],
): void {
  const seen = new Set<string>();
  items.forEach((item, index) => {
    if (seen.has(item.id)) {
      ctx.addIssue({
        code: 'custom',
        path: [...path, index, 'id'],
        message: 'Duplicate id',
      });
    }
    seen.add(item.id);
  });
}

/**
 * A week's teach-back, at {@link teachBackDocPath}. Learners read it, so it
 * rejects unknown keys: a judge-only field put here by mistake fails instead
 * of being published.
 */
export const TeachBackSpecSchema = z
  .strictObject({
    /** The week this teaches back. */
    sectionWeek: TeachBackSectionWeekSchema,
    /** Shown as the page heading. */
    title: z.string().trim().min(1),
    /** What to teach and how the session works, shown in the green room. */
    briefing: z.string().trim().min(1),
    /** The handbook chapter the judge grounds on. */
    source: TeachBackSourceSchema,
    /** The slides on screen during the session. */
    deck: TeachBackDeckSchema,
    /** The seat that answers by default; the others speak when named or asked. */
    focusSeat: z.string().trim().min(1).default(DEFAULT_TEACH_BACK_FOCUS_SEAT),
    /**
     * What the focus seat says to open the session: the section problem and
     * its own attempt at it, so the learner starts from something concrete.
     * Omitted: the students wait for the learner.
     */
    openingLine: z.string().trim().min(1).optional(),
    /**
     * What the focus seat asks after the opening line, in order, as the green
     * room previews them. Omitted: the green room shows the opening line alone.
     */
    laterQuestions: z.array(z.string().trim().min(1)).optional(),
    /**
     * The focus seat's own work, shown beside the call as its notebook, in
     * order. Omitted: no notebook.
     */
    notebook: z.array(TeachBackNotebookBlockSchema).optional(),
    /**
     * Whether the outcome panel shows live status during the session; false
     * lists the outcomes alone and reveals every status at the end.
     */
    showOutcomeProgress: z.boolean(),
    /** What the learner must show understanding of, in display order. */
    outcomes: z.array(TeachBackOutcomeSchema).min(1),
    /** Attempts allowed for the week: charged sessions and uploads. */
    maxAttempts: z
      .number()
      .int()
      .min(1)
      .default(DEFAULT_TEACH_BACK_MAX_ATTEMPTS),
    /** Longest a session may run. */
    maxSessionSeconds: z
      .number()
      .int()
      .positive()
      .default(DEFAULT_TEACH_BACK_MAX_SESSION_SECONDS),
    /** Bumped on every edit an attempt should record. */
    version: z.number().int().min(1),
  })
  .superRefine((spec, ctx) => {
    addDuplicateIdIssues(spec.outcomes, ctx, ['outcomes']);
    spec.notebook?.forEach((block, index) => {
      if (!('table' in block)) return;
      const width = block.table.header.length;
      block.table.rows.forEach((row, rowIndex) => {
        if (row.cells.length !== width) {
          ctx.addIssue({
            code: 'custom',
            path: ['notebook', index, 'table', 'rows', rowIndex, 'cells'],
            message: `Has ${String(row.cells.length)} cells; the header has ${String(width)}`,
          });
        }
      });
    });
  });

/** A week's teach-back as stored (defaults applied). */
export type TeachBackSpec = z.infer<typeof TeachBackSpecSchema>;

/**
 * One of the separate things an outcome asks for, such as one of a model's
 * random variables. The judge credits each piece on its own, and a credited
 * piece stays credited.
 */
export const TeachBackJudgePieceSchema = z.object({
  /** Unique within its outcome. */
  id: TeachBackOutcomeIdSchema,
  /**
   * What the piece is, in the judge's terms, with the forms that count.
   * Never shown to the learner or the students.
   */
  description: z.string().trim().min(1),
  /**
   * What a student asks when this is the piece still missing, as the week's
   * authors reviewed it. Omitted: the student writes its own follow-up.
   */
  question: z.string().trim().min(1).optional(),
});

/** One of the separate things an outcome asks for. */
export type TeachBackJudgePiece = z.infer<typeof TeachBackJudgePieceSchema>;

/** How the judge reads one outcome. */
export const TeachBackJudgeOutcomeSchema = z.object({
  /** The matching {@link TeachBackOutcomeSchema} id. */
  id: TeachBackOutcomeIdSchema,
  /** The understanding to look for, in the judge's terms. */
  understanding: z.string().trim().min(1),
  /** What counts as evidence of it in the learner's own lines. */
  evidenceCriteria: z.array(z.string().trim().min(1)).min(1),
  /** Wrong ideas that, stated by the learner, are misstatements. */
  misconceptions: z.array(z.string().trim().min(1)).default([]),
  /**
   * How a student presses an answer that doesn't show it yet, one per kind
   * of partial answer ("Says independent but not why: ask how they know").
   * The student's follow-up questions follow them. Omitted: the student
   * writes its own.
   */
  followUps: z.array(z.string().trim().min(1)).default([]),
  /**
   * The outcome's question asked again in other words, as the week's authors
   * reviewed them. Follow-ups may use them, and when every written follow-up
   * fails its checks the student says the one it asked longest ago (or never).
   * Omitted: the student's fixed fallback lines.
   */
  reasks: z.array(z.string().trim().min(1)).default([]),
  /**
   * The separate things the outcome asks for, in the order a student asks
   * about them. The outcome is met once every piece is credited. Omitted:
   * the judge reads the outcome as a whole.
   */
  pieces: z
    .array(TeachBackJudgePieceSchema)
    .default([])
    .superRefine((pieces, ctx) => {
      addDuplicateIdIssues(pieces, ctx, []);
    }),
});

/** How the judge reads one outcome. */
export type TeachBackJudgeOutcome = z.infer<typeof TeachBackJudgeOutcomeSchema>;

/** An authored question a student may ask about a gap. */
export const TeachBackClarifyingQuestionSchema = z.object({
  /** The outcome whose gap it probes. */
  outcomeId: TeachBackOutcomeIdSchema,
  /** The seat that asks it (first name). Omitted: the server picks. */
  seat: z.string().trim().min(1).optional(),
  /** What the student says. */
  line: z.string().trim().min(1),
});

/** An authored question a student may ask about a gap. */
export type TeachBackClarifyingQuestion = z.infer<
  typeof TeachBackClarifyingQuestionSchema
>;

/** Which outcomes a pass needs; every outcome is the only rule today. */
export const TeachBackPassRuleSchema = z.enum(['allOutcomes']);

/** A spec's judge, at {@link teachBackJudgeDocPath}: the answer key. */
export const TeachBackJudgeSpecSchema = z
  .object({
    /** One entry per spec outcome. */
    outcomes: z.array(TeachBackJudgeOutcomeSchema).min(1),
    /** The seats' starting minds. Omitted: every seat keeps its default. */
    students: StudentSeedsSchema.default([]),
    /** Questions students may ask about gaps. Omitted: none. */
    clarifyingQuestions: z.array(TeachBackClarifyingQuestionSchema).default([]),
    /**
     * The outcome the spec's opening line asks about: the focus seat follows
     * up on it like any question. Omitted: the opening asks about the whole
     * problem.
     */
    openingOutcomeId: TeachBackOutcomeIdSchema.optional(),
    /** Which outcomes a pass needs. Omitted: every outcome. */
    passRule: TeachBackPassRuleSchema.default('allOutcomes'),
    /**
     * The week's names and nouns the speech-to-text is told to expect, as a
     * plain list: never sentences, goal wording or answer words, which it
     * reads back as speech on silence. Omitted: no hint.
     */
    transcriptionTerms: z.array(z.string().trim().min(1)).default([]),
  })
  .superRefine((judge, ctx) => {
    addDuplicateIdIssues(judge.outcomes, ctx, ['outcomes']);
    const ids = new Set(judge.outcomes.map(outcome => outcome.id));
    judge.clarifyingQuestions.forEach((question, index) => {
      if (!ids.has(question.outcomeId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['clarifyingQuestions', index, 'outcomeId'],
          message: 'Not an outcome of this judge',
        });
      }
    });
    if (
      judge.openingOutcomeId !== undefined &&
      !ids.has(judge.openingOutcomeId)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['openingOutcomeId'],
        message: 'Not an outcome of this judge',
      });
    }
  });

/** A spec's judge as stored (defaults applied). */
export type TeachBackJudgeSpec = z.infer<typeof TeachBackJudgeSpecSchema>;

/** A spec with its judge, as an editor or seed holds them together. */
export const TeachBackSeedSchema = z
  .object({spec: TeachBackSpecSchema, judge: TeachBackJudgeSpecSchema})
  .superRefine(({spec, judge}, ctx) => {
    const specIds = spec.outcomes.map(outcome => outcome.id);
    const judgeIds = judge.outcomes.map(outcome => outcome.id);
    if (
      specIds.length !== judgeIds.length ||
      specIds.some(id => !judgeIds.includes(id))
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['judge', 'outcomes'],
        message: 'The judge needs exactly the spec outcomes',
      });
    }
  });

/** A spec with its judge. */
export type TeachBackSeed = z.infer<typeof TeachBackSeedSchema>;

/** How an ended attempt came out. */
export const TeachBackResultSchema = z.enum(['passed', 'not_passed']);

/** How an ended attempt came out. */
export type TeachBackResult = z.infer<typeof TeachBackResultSchema>;

/** One week's entry in a learner's summary. */
export const TeachBackWeekProgressSchema = z.object({
  /** The spec assigned on the first start; retries reuse it. */
  teachBackId: TeachBackIdSchema,
  /**
   * Charged attempts (sessions and uploads) whose charge was not given
   * back, compared with the spec's `maxAttempts`.
   */
  attemptCount: z.number().int().nonnegative(),
  /**
   * The week's newest attempt, a session or an upload. Starts and uploads
   * are serialized per week, so it is the only one that can be live.
   */
  latestAttemptId: z.string(),
  /**
   * The attempt the week's credit rests on: the first passing session or
   * upload, unless the learner chose another eligible one; null while the
   * week is uncredited.
   */
  creditedAttemptId: z.string().nullable(),
  /** When `creditedAttemptId` last changed; null while it is null. */
  creditedAt: FirestoreTimestampSchema.nullable(),
  /** The latest ended session's result; null before any ends. */
  lastResult: TeachBackResultSchema.nullable(),
});

/** One week's entry in a learner's summary. */
export type TeachBackWeekProgress = z.infer<typeof TeachBackWeekProgressSchema>;

/**
 * A learner's summary at {@link teachBackSummaryDocPath}, keyed by the
 * 1-based section week as a string.
 */
export const TeachBackSummarySchema = z.record(
  z.string().regex(/^[1-9]\d*$/),
  TeachBackWeekProgressSchema,
);

/** A learner's summary, keyed by section week. */
export type TeachBackSummary = z.infer<typeof TeachBackSummarySchema>;

/**
 * An attempt's id: the `requestId` a client generates (`crypto.randomUUID()`)
 * for one start request. Being a UUID keeps it safe in document and Storage
 * paths.
 */
export const TeachBackAttemptIdSchema = z.uuid();

/**
 * How an attempt was made: `live`, a recorded session with the simulated
 * students that the judge checks, or `upload`, a video the learner recorded
 * themselves, which is never judged.
 */
export const TeachBackAttemptKindSchema = z.enum(['live', 'upload']);

/** How an attempt was made. */
export type TeachBackAttemptKind = z.infer<typeof TeachBackAttemptKindSchema>;

/**
 * Where an attempt is: `allocated` (created, not yet taught into),
 * `in_progress` (a session holds it), `ended` (a session saved with a
 * result, or an upload created), `interrupted` (a session abandoned without
 * a result), or `removed` (an upload the learner removed).
 */
export const TeachBackAttemptStatusSchema = z.enum([
  'allocated',
  'in_progress',
  'ended',
  'interrupted',
  'removed',
]);

/** Where an attempt is. */
export type TeachBackAttemptStatus = z.infer<
  typeof TeachBackAttemptStatusSchema
>;

/** How far the learner's lines show an outcome. */
export const TeachBackOutcomeStatusSchema = z.enum([
  'not_yet',
  'partial',
  'met',
]);

/** How far the learner's lines show an outcome. */
export type TeachBackOutcomeStatus = z.infer<
  typeof TeachBackOutcomeStatusSchema
>;

/** A learner's line quoted as evidence. */
export const TeachBackQuoteSchema = z.object({
  /** The quoted line's `seq` in the transcript. */
  seq: z.number().int().nonnegative(),
  /** The learner's words. */
  quote: z.string().max(TEACH_BACK_MAX_QUOTE_CHARS),
});

/** A learner's line quoted as evidence. */
export type TeachBackQuote = z.infer<typeof TeachBackQuoteSchema>;

/** Something wrong the learner said about an outcome. */
export const TeachBackMisstatementSchema = z.object({
  /** What the learner said. */
  said: TeachBackQuoteSchema,
  /** What is actually true, in one sentence. */
  correction: z.string(),
  /** The learner's later line that corrected it; null while it stands. */
  resolvedBy: TeachBackQuoteSchema.nullable(),
});

/** Something wrong the learner said about an outcome. */
export type TeachBackMisstatement = z.infer<typeof TeachBackMisstatementSchema>;

/** The judge's view of one outcome. */
export const TeachBackOutcomeCheckSchema = z.object({
  /** The outcome's id. */
  id: TeachBackOutcomeIdSchema,
  /** How far the learner's lines show it. */
  status: TeachBackOutcomeStatusSchema,
  /** The learner's lines that show it, best first. */
  evidence: z.array(TeachBackQuoteSchema).max(TEACH_BACK_MAX_EVIDENCE_QUOTES),
  /** What is still missing; "" once met. */
  missing: z.string(),
  /**
   * Wrong things said about it, oldest first. Every open one is kept, since
   * an open one blocks a pass; resolved ones are capped.
   */
  misstatements: z
    .array(TeachBackMisstatementSchema)
    .refine(
      misstatements =>
        misstatements.filter(m => m.resolvedBy !== null).length <=
        TEACH_BACK_MAX_RESOLVED_MISSTATEMENTS,
      'Too many resolved misstatements',
    ),
  /**
   * For an outcome with pieces, the learner's line credited for each piece
   * so far, by piece id; never taken back. Omitted: none, as on outcomes
   * without pieces and on attempts saved before them.
   */
  pieces: z.record(z.string(), TeachBackQuoteSchema).default({}),
});

/** The judge's view of one outcome. */
export type TeachBackOutcomeCheck = z.infer<typeof TeachBackOutcomeCheckSchema>;

/**
 * The judge check run on a line: the outcomes it changed, or what went wrong.
 * Only changes are kept so a long session's attempt stays small; replaying
 * them in order rebuilds each outcome at any line.
 */
export const TeachBackJudgeCheckSchema = z.union([
  z.object({
    /** Each outcome this check changed, as it now stands; empty for none. */
    changed: z.array(TeachBackOutcomeCheckSchema),
    /** When it landed. */
    at: FirestoreTimestampSchema,
  }),
  z.object({
    /** What went wrong. */
    error: z.string(),
    /** When it landed. */
    at: FirestoreTimestampSchema,
  }),
]);

/** The judge check run on a line. */
export type TeachBackJudgeCheck = z.infer<typeof TeachBackJudgeCheckSchema>;

/**
 * One line of an attempt's transcript, in the shape simteach's
 * `SessionTranscript` writes for every section activity (see
 * `SimTeachPuzzleTranscriptLineSchema` in `simTeachPuzzleProgress.ts`).
 */
export const TeachBackTranscriptLineSchema = z.object({
  /** Position, ordered by `at`. */
  seq: z.number().int().nonnegative(),
  /** When the line was said. */
  at: FirestoreTimestampSchema,
  /** Who said it. */
  speaker: z.enum(['teacher', 'student']),
  /** What was said. */
  text: z.string(),
  /** Said live; a Teach it back session replays no opening. */
  source: z.literal('live'),
  /** A teacher line's pause, or the pause a student line answers. */
  turnId: z.string().optional(),
  /** A student line's seat. */
  studentId: z.number().int().optional(),
  /** A student line's first name. */
  name: z.string().optional(),
  /** A student line asked about a gap: the outcome it probed. */
  clarifies: TeachBackOutcomeIdSchema.optional(),
  /** Each check run on this line, at most once per kind. */
  checks: z.object({
    /** The judge check this line prompted. */
    judge: TeachBackJudgeCheckSchema.optional(),
  }),
});

/** One line of an attempt's transcript. */
export type TeachBackTranscriptLine = z.infer<
  typeof TeachBackTranscriptLineSchema
>;

/** One attempt, at {@link teachBackAttemptDocPath}. */
export const TeachBackAttemptSchema = z.object({
  /**
   * This doc's id: the start request's `requestId`, so a retry finds it
   * instead of creating another. A found attempt answers a retry only while
   * it is still `allocated`.
   */
  attemptId: TeachBackAttemptIdSchema,
  /** The learner. */
  uid: z.string(),
  /** The course the spec belongs to. */
  courseId: z.string(),
  /** The week it teaches back. */
  sectionWeek: TeachBackSectionWeekSchema,
  /** The spec taught. */
  teachBackId: TeachBackIdSchema,
  /** How it was made. Omitted: `live`, which every attempt once was. */
  kind: TeachBackAttemptKindSchema.default('live'),
  /**
   * The spec's `version`: when the attempt was created, then the one the
   * session that took it ran.
   */
  version: z.number().int().min(1),
  /**
   * An admin's preview of a spec: never charged or credited, and never in
   * the summary. Only a live attempt is a preview.
   */
  preview: z.boolean(),
  /** Where the attempt is. */
  status: TeachBackAttemptStatusSchema,
  /** When it was allocated (a session) or created (an upload). */
  createdAt: FirestoreTimestampSchema,
  /**
   * When a session took it, once its websocket could be taught into; null
   * while allocated, and always for an upload. The session ends
   * `maxSessionSeconds` after.
   */
  startedAt: FirestoreTimestampSchema.nullable(),
  /** When it ended or was interrupted; null before. */
  endedAt: FirestoreTimestampSchema.nullable(),
  /**
   * When it counted against the cap. simteach charges a session in the same
   * transaction that takes it, so it is set exactly when `startedAt` is
   * (never for a preview); an upload is charged when it is created. Never
   * cleared, so it also guards against charging twice.
   */
  chargedAt: FirestoreTimestampSchema.nullable(),
  /**
   * When its charge was given back: a session that was interrupted (an
   * interrupted attempt never counts), by the start that interrupted it or
   * by a session that took it but never ran, or an upload the learner
   * removed; null otherwise.
   */
  chargeReturnedAt: FirestoreTimestampSchema.nullable(),
  /**
   * The take id of the session that took it, unique to that session; null
   * while allocated. simteach takes only an `allocated` attempt, and ends
   * (saves or, if the session never ran, interrupts) only an `in_progress`
   * attempt holding its own take id, so a start that interrupted it wins.
   */
  sessionId: z.string().nullable(),
  /** Every line of the session, oldest first; empty until it ends. */
  transcript: z.array(TeachBackTranscriptLineSchema),
  /** Every outcome as the attempt ended; empty until it ends. */
  outcomes: z.array(TeachBackOutcomeCheckSchema),
  /**
   * The outcomes the students stopped asking about at their question cap
   * (simteach's `MAX_ASKS_PER_OUTCOME`) without the judge counting them as
   * shown, for staff to review. Omitted: none, as on attempts saved before
   * the cap.
   */
  cappedOutcomes: z
    .array(
      z.object({
        /** The outcome's id. */
        id: TeachBackOutcomeIdSchema,
        /** The `seq` of the learner's line the cap came after. */
        seq: z.number().int().nonnegative(),
      }),
    )
    .default([]),
  /** How a session came out; null unless ended, and always for an upload. */
  result: TeachBackResultSchema.nullable(),
});

/** One attempt. */
export type TeachBackAttempt = z.infer<typeof TeachBackAttemptSchema>;

/**
 * Path of a week's teach-back spec.
 *
 * @param args.courseId - The course.
 * @param args.teachBackId - The spec.
 * @return `course/{courseId}/teachBacks/{teachBackId}`.
 * @throws Never.
 */
export function teachBackDocPath({
  courseId,
  teachBackId,
}: {
  courseId: string;
  teachBackId: string;
}): string {
  return `course/${courseId}/${TEACH_BACKS_COLLECTION}/${teachBackId}`;
}

/**
 * Path of a spec's judge.
 *
 * @param args.courseId - The course.
 * @param args.teachBackId - The spec.
 * @return `course/{courseId}/teachBacks/{teachBackId}/private/judge`.
 * @throws Never.
 */
export function teachBackJudgeDocPath({
  courseId,
  teachBackId,
}: {
  courseId: string;
  teachBackId: string;
}): string {
  return `${teachBackDocPath({courseId, teachBackId})}/${TEACH_BACK_PRIVATE_COLLECTION}/${TEACH_BACK_JUDGE_DOC_ID}`;
}

/**
 * Path of a learner's summary.
 *
 * @param args.uid - The learner.
 * @param args.courseId - The course.
 * @return `users/{uid}/{courseId}/teachBack`.
 * @throws Never.
 */
export function teachBackSummaryDocPath({
  uid,
  courseId,
}: {
  uid: string;
  courseId: string;
}): string {
  return `users/${uid}/${courseId}/${TEACH_BACK_SUMMARY_DOC_ID}`;
}

/**
 * Path of one attempt.
 *
 * @param args.uid - The learner.
 * @param args.courseId - The course.
 * @param args.attemptId - The attempt.
 * @return `users/{uid}/{courseId}/teachBack/teachBackAttempts/{attemptId}`.
 * @throws Never.
 */
export function teachBackAttemptDocPath({
  uid,
  courseId,
  attemptId,
}: {
  uid: string;
  courseId: string;
  attemptId: string;
}): string {
  return `${teachBackSummaryDocPath({uid, courseId})}/${TEACH_BACK_ATTEMPTS_COLLECTION}/${attemptId}`;
}

/**
 * Storage path of an attempt's video: a session's recording or an upload.
 *
 * @param args.courseId - The course.
 * @param args.uid - The learner.
 * @param args.attemptId - The attempt.
 * @return `teachBackRecordings/{courseId}/{uid}/{attemptId}`.
 * @throws Never.
 */
export function teachBackRecordingPath({
  courseId,
  uid,
  attemptId,
}: {
  courseId: string;
  uid: string;
  attemptId: string;
}): string {
  return `${TEACH_BACK_RECORDINGS_FOLDER}/${courseId}/${uid}/${attemptId}`;
}
